import { useEffect } from 'react';
import { useLogger, useUserContext } from '@vp/ubik-context';
import { FragmentLogger } from '@vp/ubik-logging';
import { Resource } from 'i18next';
import { AppRoutes, BriefUbikProviders } from '@99designs/briefs';
import { CORTEX_DOMAIN_TAG } from '@99designs/design-services-common';
import { BriefsEnvironment } from '@99designs/feature-design-services-brief';
import { SupportedLocale } from '@99designs/i18n';
import {
    OPENPANEL_TRIAL_API_URL,
    OPENPANEL_TRIAL_CLIENT_ID,
    OPENPANEL_TRIAL_SCRIPT_URL,
    OpenPanelLoader,
    QualarooLoader,
} from '@99designs/tracking';
import { LOGGER_FRAGMENT_ID } from '../lib/consts';

export interface Props {
    localeReference: string;
    resources: Resource;
    locale: string;
    environment?: BriefsEnvironment;
}

const openPanelEnabled = process.env.BRIEFS_OPENPANEL_ENABLED === 'true';

function getEnvironent(env: string): BriefsEnvironment {
    if (env === 'development') {
        return 'DEV';
    }
    if (env === 'staging') {
        return 'STAGING';
    }
    return 'PROD';
}

export const App = ({ resources, locale }: Props) => {
    const logger = useLogger() as FragmentLogger;
    const userContext = useUserContext();
    logger.setContext({ fragmentId: LOGGER_FRAGMENT_ID, 'vista.team_tag': CORTEX_DOMAIN_TAG });

    useEffect(() => {
        const hasIntelligentBrief =
            document.referrer && document.referrer.includes('/brief/start?');

        if (hasIntelligentBrief !== null && window._kiq) {
            window._kiq.push(['set', { intelligent_brief_user: hasIntelligentBrief }]);
        }
    });

    const currentCulture = locale as SupportedLocale;
    const env = getEnvironent(userContext.environment || 'production');
    return (
        <BriefUbikProviders locale={locale} resources={resources} environment={env}>
            <QualarooLoader />
            {openPanelEnabled && (
                <OpenPanelLoader
                    enabled
                    clientId={OPENPANEL_TRIAL_CLIENT_ID}
                    apiUrl={OPENPANEL_TRIAL_API_URL}
                    scriptUrl={OPENPANEL_TRIAL_SCRIPT_URL}
                />
            )}
            <AppRoutes currentCulture={currentCulture} />
        </BriefUbikProviders>
    );
};
